/**
 * Shared wording for the Step 4 (Supporting evidence) removal modals.
 *
 * Four pages warn a user before removing evidence they've already given
 * us:
 *  - /evidence-request — answering "No" to having medical records
 *  - /medical-records — unchecking a record type (VA or private)
 *  - /private-medical-records-access — (4142/request vs upload)
 *  - /additional-evidence-intro — answering "No" to supporting documents
 *
 * They describe overlapping sets of the same three kinds of evidence, so the
 * names, counts, and sentence construction live here rather than being
 * rewritten per page.
 */
import React from 'react';

/** "3 other medical centers", "1 other medical center". */
const countOf = (count, noun) =>
  `${count} other ${noun}${count === 1 ? '' : 's'}`;

/**
 * How to name each kind of evidence in a list, and how to count whatever
 * doesn't fit. The fallbacks cover part-filled entries a user hasn't
 * named yet.
 */
export const EVIDENCE_KINDS = {
  vaFacility: {
    nameOf: item =>
      item?.treatmentCenterName || 'VA or military medical facility',
    overflowLabel: count => countOf(count, 'medical center'),
  },
  provider: {
    nameOf: item => item?.providerFacilityName || 'Private healthcare provider',
    overflowLabel: count => countOf(count, 'healthcare provider'),
  },
  // Uploaded records
  record: {
    nameOf: item => item?.uploadedDocument?.name || 'Uploaded record',
    overflowLabel: count => countOf(count, 'medical record'),
  },
  // The same uploads, counted as files, where the surrounding copy says
  // "files" rather than "medical records".
  file: {
    nameOf: item => item?.uploadedDocument?.name || 'Uploaded record',
    overflowLabel: count => countOf(count, 'file'),
  },
  // supporting documents and additional forms
  document: {
    nameOf: item => item?.uploadedDocument?.name || 'Supporting document',
    overflowLabel: count => countOf(count, 'file'),
  },
};

/**
 * Builds the modal's item groups, dropping empty ones and chaining the rest
 * into a single passage: the first group shown leads with "If you do so",
 * and any after it say "We'll also".
 *
 * Each entry supplies the verb phrase for its own sentence — for example
 * `delete these files you uploaded` becomes "If you do so, we'll delete
 * these files you uploaded:" or "We'll also delete these files you
 * uploaded:" depending on where it lands.
 *
 * @param {Array<{ items: object[], kind: keyof EVIDENCE_KINDS, phrase: string }>} entries
 * @returns {Array<{ intro: React.ReactNode, items: string[], overflowLabel: Function }>}
 */
export const buildItemGroups = entries =>
  entries
    .filter(({ items }) => items.length)
    .map(({ items, kind, phrase }, index) => {
      const { nameOf, overflowLabel } = EVIDENCE_KINDS[kind];
      return {
        intro: (
          <p>
            {index === 0
              ? `If you do so, we’ll ${phrase}:`
              : `We’ll also ${phrase}:`}
          </p>
        ),
        items: items.map(nameOf),
        overflowLabel,
      };
    });

/**
 * Joins what was taken out into one success sentence, e.g. "We’ve removed
 * information about your healthcare providers and deleted the medical
 * records you uploaded from this claim."
 *
 * @param {string[]} actions past-tense phrases, in the order they read
 * @returns {string}
 */
export const removalAlert = actions =>
  `We’ve ${actions.join(' and ')} from this claim.`;

/** True when any of the given lists has something in it. */
export const anyEvidence = (...lists) => lists.some(list => list.length > 0);
